import { Link } from 'react-router-dom';
import type { ArticleItem } from '@/mocks/articles';
import { getCategoryName } from '@/utils/content';

type Variant = 'hero' | 'standard' | 'compact' | 'row';

interface ArticleCardProps {
  article: ArticleItem;
  variant?: Variant;
}

export default function ArticleCard({ article, variant = 'standard' }: ArticleCardProps) {
  const categoryName = getCategoryName(article.categorySlug);

  const badge = (
    <Link
      to={`/category/${article.categorySlug}`}
      className="relative z-10 inline-flex items-center gap-1 rounded-full bg-accent-500/15 px-2.5 py-1 text-xs font-medium text-accent-300 transition-colors hover:bg-accent-500/25"
    >
      {categoryName}
    </Link>
  );

  if (variant === 'compact') {
    return (
      <article className="group relative flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100">
        <Link
          to={`/article/${article.id}`}
          className="relative z-10 block h-16 w-24 shrink-0 overflow-hidden rounded-md"
        >
          <img
            src={article.cover}
            alt={`${article.title} - 深夜爆料`}
            title={`${article.title} 深夜爆料 新瓜速递`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 text-sm font-medium leading-snug text-foreground-200 before:absolute before:inset-0 before:content-[''] group-hover:text-primary-300"
          >
            {article.title}
          </Link>
          <p className="mt-1.5 text-xs text-foreground-500">{article.date}</p>
        </div>
      </article>
    );
  }

  if (variant === 'row') {
    return (
      <article className="group relative flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-500/40 sm:flex-row sm:p-4">
        <Link
          to={`/article/${article.id}`}
          className="relative z-10 block h-40 w-full shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-44"
        >
          <img
            src={article.cover}
            alt={`${article.title} - 深夜爆料`}
            title={`${article.title} 深夜爆料 新瓜速递`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-col">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {badge}
            {article.isLive ? (
              <span className="inline-flex items-center gap-1 text-xs text-primary-400">
                <i className="ri-flashlight-fill"></i>
                首发
              </span>
            ) : null}
          </div>
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-100 before:absolute before:inset-0 before:content-[''] group-hover:text-primary-300 md:text-lg"
          >
            {article.title}
          </Link>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
            {article.excerpt}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-600">
            <span className="inline-flex items-center gap-1">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.date}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-accent-400"></i>
              {article.hot.toLocaleString()}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'hero') {
    return (
      <article className="group relative overflow-hidden rounded-lg border border-background-200">
        <div className="relative h-[300px] w-full overflow-hidden md:h-[420px]">
          <img
            src={article.cover}
            alt={`${article.title} - 深夜爆料`}
            title={`${article.title} 深夜爆料 新瓜速递`}
            className="h-full w-full object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/55 to-transparent"></div>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {badge}
            {article.isLive ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-950">
                <i className="ri-flashlight-fill"></i>
                今夜首发
              </span>
            ) : null}
          </div>
          <h3 className="max-w-2xl font-heading text-xl font-bold leading-snug text-foreground-50 md:text-3xl">
            <Link
              to={`/article/${article.id}`}
              className="before:absolute before:inset-0 before:content-[''] transition-colors group-hover:text-primary-300"
            >
              {article.title}
            </Link>
          </h3>
          <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-foreground-300 sm:block">
            {article.excerpt}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-foreground-400">
            <span className="inline-flex items-center gap-1">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.date}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-accent-400"></i>
              {article.hot.toLocaleString()} 热度
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-500/40">
      <div className="relative h-44 w-full overflow-hidden md:h-48">
        <img
          src={article.cover}
          alt={`${article.title} - 深夜爆料`}
          title={`${article.title} 深夜爆料 新瓜速递`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-background-950/75 px-2.5 py-1 text-xs text-primary-300 backdrop-blur-sm">
          <i className="ri-time-line"></i>
          {article.liveTime}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2.5 flex flex-wrap items-center gap-2">
          {badge}
          {article.isLive ? (
            <span className="inline-flex items-center gap-1 text-xs text-primary-400">
              <i className="ri-flashlight-fill"></i>
              首发
            </span>
          ) : null}
        </div>
        <h3 className="font-heading text-base font-semibold leading-snug text-foreground-100">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 before:absolute before:inset-0 before:content-[''] transition-colors group-hover:text-primary-300"
          >
            {article.title}
          </Link>
        </h3>
        <p className="mt-2.5 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-500">
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200/80 pt-3 text-xs text-foreground-600">
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-3-line"></i>
            {article.author}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-line text-accent-400"></i>
            {article.hot.toLocaleString()}
          </span>
        </div>
      </div>
    </article>
  );
}